<script lang="ts">
  import type { PageData } from "./$types";
  import { PortableText } from "@portabletext/svelte";
  import { selectAreasExtended as areas } from "$lib/utils/areaUtils";
  import { contentBlockComponents } from "$lib/portableText/utils";
  import { page } from "$app/state";
  import InfoBanner from "$lib/InfoBanner.svelte";
  import TypeformButton from "$lib/TypeformButton.svelte";
  import Select from "$lib/SelectSingle.svelte";

  type FastPrisRaw = {
    NO1: string | null;
    NO2: string | null;
    NO3: string | null;
    NO4: string | null;
    NO5: string | null;
    Periode: { startDateTime: string; endDateTime: string | null }[];
    Varighet: string;
    "Pris satt": string;
    "Produkt NO1": string | null;
    "Produkt NO2": string | null;
    "Produkt NO3": string | null;
    "Produkt NO4": string | null;
    "Produkt NO5": string | null;
    Elsertifikat: string;
  };

  type ProcessedPrice = {
    omrade: string;
    price: number;
    elsertifikat: number;
    varighet: string;
    produktNavn: string | null;
  };

  const baseFastbelop = 39;
  const priceAreas = ["NO1", "NO2", "NO3", "NO4", "NO5"] as const;

  /**
   * Calculates the final price including elsertifikat and VAT.
   * Returns 0 if price is 0 (unavailable).
   * NO4 is VAT-exempt, other areas get 25% VAT.
   * @param price - Base price in øre/kWh
   * @param elsertifikat - Elsertifikat cost to add
   * @param omrade - Price area code (e.g. "NO1", "NO4")
   * @returns Final price with elsertifikat and VAT applied
   */
  const calcFixedPrice = (price: number, elsertifikat: number, omrade: string): number =>
    price && (price + elsertifikat) * (omrade.toUpperCase() === "NO4" ? 1 : 1.25);

  /**
   * Transforms the raw API data into a flat array of processed prices per area and duration.
   */
  const processRawPrices = (data: FastPrisRaw[]): ProcessedPrice[] => {
    const result: ProcessedPrice[] = [];

    for (const item of data) {
      const elsertifikat = parseFloat(item.Elsertifikat) || 0;
      const varighet = item.Varighet;

      for (const area of priceAreas) {
        const rawPrice = item[area];
        if (rawPrice === null) continue;

        const basePrice = parseFloat(rawPrice);
        if (isNaN(basePrice)) continue;

        const produktNavn = item[`Produkt ${area}` as keyof FastPrisRaw] as string | null;

        // Add the price with elsertifikat included
        result.push({
          omrade: area,
          price: calcFixedPrice(basePrice, elsertifikat, area),
          elsertifikat,
          varighet,
          produktNavn,
        });

        // NO4 also gets a NO4-2 variant (Nord-Troms/Finnmark) without elsertifikat in calculation
        if (area === "NO4") {
          result.push({
            omrade: "NO4-2",
            price: basePrice, // Original price without elsertifikat adjustment
            elsertifikat,
            varighet,
            produktNavn,
          });
        }
      }
    }

    return result;
  };

  /**
   * Gets the price for a specific area and binding duration.
   */
  const getFixedPriceCost = (
    prices: ProcessedPrice[],
    omrade: string | undefined,
    bindingYears: string | undefined
  ): number | undefined => {
    if (!omrade || !bindingYears) return undefined;
    const varighetLabel = `${bindingYears} år`;
    return prices.find((p) => p.omrade === omrade && p.varighet === varighetLabel)?.price;
  };

  const bindings = [
    { value: "1", label: "1 år" },
    { value: "3", label: "3 år" },
  ];

  const formatPrice = (price?: number): string =>
    typeof price === "number" ? price.toFixed(2).replace(".", ",") : "";

  let {
    content,
    fixedPrices,
  }: {
    content: PageData["fixedPriceContent"];
    fixedPrices: PageData["fixedPrices"];
  } = $props();

  let bindingsSelectValues = bindings.map((b) => b.value);
  let selectedBindingstid = $state(bindings[0].value);
  let selectedItemVal = $state("NO4");

  const selectedItem = $derived(areas.find((x) => x.value === selectedItemVal));
  const selectedBindingItem = $derived(bindings.find((b) => b.value === selectedBindingstid));
  const orderHref = $derived(content?.order_href || "https://ishavskraft.typeform.com/to/euUF2JJe");
  const omrade = $derived(selectedItem?.label);
  const fastbelop = $derived(
    selectedItem?.value !== "NO4" && selectedItem?.value !== "NO4-2"
      ? baseFastbelop * 1.25
      : baseFastbelop
  );
  let lang = $derived(page.params?.lang ? page.params.lang : undefined);

  const fastPriser = $derived(processRawPrices(fixedPrices as FastPrisRaw[]));
  const fastprisCost = $derived(
    getFixedPriceCost(fastPriser, selectedItem?.value, selectedBindingItem?.value)
  );
  const customParameters = $derived(
    selectedBindingItem && {
      produkt: `Fastpris ${selectedBindingItem.label}`,
      fastbelop: formatPrice(fastbelop),
      ...(omrade ? { omrade } : {}),
      ...(fastprisCost ? { strompris: "" + formatPrice(fastprisCost) } : {}),
      product_id: content?.contract?.product_id || "",
    }
  );
</script>

<div class="flex flex-col items-center responsive-grid">
  <div lang={lang ? "nb" : undefined} class="max-w-sm prose w-full">
    <p class="mb-2"><label for="prisomraade"><strong>Prisområde</strong></label></p>
    <Select class="w-full" bind:value={selectedItemVal} name="prisomraade" id="prisomraade">
      {#each areas as area (area.value)}
        <option value={area.value}>
          {area.label}
        </option>
      {/each}
    </Select>

    <ul class="list-none p-0 grid grid-cols-2 gap-2">
      {#each bindingsSelectValues as val (val)}
        <li>
          <label for={`bindingstid-${val}`} class="button w-full">
            <input
              class="accent-$primary-strong"
              type="radio"
              name="bindingstid"
              id={`bindingstid-${val}`}
              value={val}
              bind:group={selectedBindingstid}
              checked={val === selectedBindingstid}
            />
            {`${val} år`}
          </label>
        </li>
      {/each}
    </ul>

    {#if fastprisCost}
      <div class="flex justify-between mt-$s-4">
        <p><strong>Avtale</strong></p>
        <p class="text-right flex-1">
          {`Fastpris ${selectedBindingItem?.label} ${selectedItem?.label}`}
        </p>
      </div>
      <div class="flex justify-between">
        <p class="mt-0"><strong>Strømpris</strong></p>
        <p class="mt-0">{formatPrice(fastprisCost)} øre/kWh</p>
      </div>
      <div class="flex justify-between">
        <p class="m-0"><strong>Månedsbeløp</strong></p>
        <p class="m-0">+ {formatPrice(fastbelop)} kr</p>
      </div>
      {#if selectedItem?.value.startsWith("NO4")}
        <p class="text-base text-center"><small>Prisen inkluderer fritak for MVA</small></p>
      {:else}
        <p class="text-base text-center"><small>Prisen inkluderer MVA</small></p>
      {/if}
      <p class="text-center my-6">
        <TypeformButton
          arrow
          primary
          class="button-drop-shadow"
          {lang}
          url={orderHref}
          {customParameters}
        >
          {content?.order_button_label || "Bestill strømavtalen"}
        </TypeformButton>
      </p>
    {:else}
      <InfoBanner type="warning">Denne avtalen er ikke tilgjengelig akkurat nå.</InfoBanner>
    {/if}
  </div>

  <div>
    {#if content?.contract?.content}
      <PortableText
        value={content.contract.content}
        components={contentBlockComponents}
        onMissingComponent={false}
      />
    {/if}

    {#if content?.contract?.documents && content.contract.documents.length > 0}
      <ul class="my-8">
        {#each content.contract.documents as doc (doc)}
          <li>
            <a href={doc.url} target="_blank">{doc.title ?? "Fil uten navn"}</a>
          </li>
        {/each}
      </ul>
    {/if}

    {#if content?.contract?.finePrint}
      <div class="my-3 children-my-1 text-base">
        <PortableText
          value={content.contract.finePrint}
          components={contentBlockComponents}
          onMissingComponent={false}
        />
      </div>
    {/if}
  </div>
</div>

<style lang="postcss">
  @media (--tablet) {
    .responsive-grid {
      flex-direction: row-reverse;
      gap: var(--s-16);
      justify-content: space-between;
      align-items: start;
    }
    .responsive-grid > div {
      flex: 1;
    }
  }
</style>
